import { isInternalUser } from '@steerfi/shared';
import { AlertCircle, Download, Loader2, Receipt, RefreshCcw } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  getPaymentById,
  getPaymentReceiptUrl,
  getPaymentReissueEligibility,
  type PaymentReissueEligibility,
  type PaymentWithRelations,
  reissuePayment,
} from '@/api/payments';
import { getSupplierAddConfig } from '@/api/suppliers';
import { PaymentDetailsBody } from '@/components/payment-details';
import { ReissuePaymentDialog } from '@/components/reissue-payment-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useDocumentTitle } from '@/contexts/document-title-context';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { formatDate } from '@/lib/utils';

export const PaymentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [payment, setPayment] = useState<PaymentWithRelations | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [externalRefLabel, setExternalRefLabel] = useState<string>('External Ref');
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [reissueOpen, setReissueOpen] = useState(false);
  const [reissueSubmitting, setReissueSubmitting] = useState(false);
  const [eligibility, setEligibility] = useState<PaymentReissueEligibility | null>(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);

  const { user } = useKeycloakAuth();
  const showMetadataTab = isInternalUser(user?.roles);

  const fetchPayment = useCallback(async () => {
    if (!id) {
      setIsLoading(false);
      setNotFound(true);
      return;
    }
    setIsLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const response = await getPaymentById(id);
      setPayment(response);
      setEligibility(response.reissue_eligibility ?? null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load payment';
      if (/not found/i.test(message)) {
        setNotFound(true);
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPayment();
  }, [fetchPayment]);

  // Wait for the payment to finish loading before calling add-config so we
  // don't fire with undefined party IDs during the initial render.
  useEffect(() => {
    if (isLoading) return;
    const partyId = payment?.owner_party?.id ?? payment?.payer_party?.id;
    if (!partyId) return;
    getSupplierAddConfig(partyId)
      .then((config) => {
        setExternalRefLabel(config.externalRefDisplayName?.trim() || 'External Ref');
      })
      .catch(() => {});
  }, [payment?.owner_party?.id, payment?.payer_party?.id, isLoading]);

  const isReturned = payment?.status?.code === 'RETURNED';
  const isReissued = payment?.status?.code === 'REISSUED';
  const canOfferReissue = isReturned;

  const supplierDetailPath = useMemo(() => {
    const supplierId = payment?.payee_party?.id ?? payment?.payee_party_id ?? null;
    return supplierId ? `/suppliers/${supplierId}` : '/suppliers';
  }, [payment]);

  const loadEligibility = useCallback(async () => {
    if (!id || !isReturned) {
      setEligibility(null);
      return;
    }
    setEligibilityLoading(true);
    try {
      const result = await getPaymentReissueEligibility(id);
      setEligibility(result);
      setPayment((current) => (current ? { ...current, reissue_eligibility: result } : current));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load reissue eligibility');
      setEligibility(null);
    } finally {
      setEligibilityLoading(false);
    }
  }, [id, isReturned]);

  const handleDownloadReceipt = useCallback(async () => {
    if (!payment) return;
    setReceiptLoading(true);
    try {
      const result = await getPaymentReceiptUrl(payment.id);
      if (result?.signed_url) {
        window.open(result.signed_url, '_blank');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to download receipt');
    } finally {
      setReceiptLoading(false);
    }
  }, [payment]);

  const handleOpenReissue = useCallback(() => {
    setEligibility(payment?.reissue_eligibility ?? null);
    setReissueOpen(true);
  }, [payment]);

  const handleReissue = useCallback(
    async (paymentMethodAccountId: string) => {
      if (!id) return;
      setReissueSubmitting(true);
      try {
        const result = await reissuePayment(id, paymentMethodAccountId);
        toast.success(`Reissue created — ${result.reissue_file_name}`);
        setReissueOpen(false);
        await fetchPayment();
        navigate(`/payments/${result.reissued_payment_id}`);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to reissue payment');
      } finally {
        setReissueSubmitting(false);
      }
    },
    [id, fetchPayment, navigate]
  );

  // Hoisted above the early returns so the document title can reuse the same
  // label the heading shows; hooks cannot be called conditionally.
  const title = payment ? (payment.external_id ?? `${payment.id.slice(0, 8)}…`) : null;

  useDocumentTitle(title && `Payment ${title}`);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <p className="text-sm text-muted-foreground">Loading payment…</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <p className="text-sm text-muted-foreground">Payment not found.</p>
        <Link to="/payments" className="text-sm underline underline-offset-4">
          Back to Payments
        </Link>
      </div>
    );
  }

  if (error || !payment) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive text-sm">
          {error ?? 'Failed to load payment.'}
        </div>
        <Link to="/payments" className="text-sm underline underline-offset-4">
          Back to Payments
        </Link>
      </div>
    );
  }

  const isReceiptEligible = payment.status?.code === 'COMPLETED';
  const returnInfo = payment.return_info;
  const returnCode = returnInfo?.return_code ?? 'N/A';
  const returnReason = returnInfo?.return_reason ?? 'Returned by bank';
  const returnDate = returnInfo?.returned_at ? formatDate(returnInfo.returned_at) : null;
  const supplierName =
    payment.payee_name || payment.payee_party?.party_name || eligibility?.supplier?.name;

  return (
    <div className="flex flex-col gap-4 px-4 py-4 md:gap-5 md:py-6 lg:px-6">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
        <header className="flex flex-col gap-4">
          <nav className="text-xs text-muted-foreground">
            <Link to="/payments" className="hover:text-foreground">
              Payments
            </Link>
            <span className="mx-1.5">/</span>
            <span className="text-foreground">{title}</span>
          </nav>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="flex items-center gap-2 truncate text-2xl font-bold tracking-tight">
                  <Receipt className="h-5 w-5 text-primary shrink-0" />
                  Payment {title}
                </h1>
                {isReturned && <Badge variant="returned">Returned</Badge>}
                {isReissued && <Badge variant="outline">Reissued</Badge>}
              </div>
              {payment.external_reference && (
                <p className="mt-1 text-sm text-muted-foreground">
                  {externalRefLabel}: {payment.external_reference}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {canOfferReissue && (
                <Button type="button" onClick={handleOpenReissue} disabled={eligibilityLoading}>
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Reissue Payment
                </Button>
              )}
              {isReceiptEligible && (
                <Button variant="outline" onClick={handleDownloadReceipt} disabled={receiptLoading}>
                  {receiptLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Download Receipt
                </Button>
              )}
            </div>
          </div>
        </header>

        {isReturned && (
          <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-semibold">Payment returned by bank</p>
              <p className="text-sm">
                ACH return {returnCode} - {returnReason}
                {returnDate ? ` · ${returnDate}` : ''}
              </p>
              <p className="text-sm">
                Review the{' '}
                <Link
                  to={supplierDetailPath}
                  className="font-medium underline underline-offset-2 hover:text-red-950 dark:hover:text-red-50"
                >
                  supplier&apos;s payment method
                </Link>{' '}
                then click Reissue Payment.
              </p>
            </div>
          </div>
        )}

        <PaymentDetailsBody
          payment={payment}
          externalRefLabel={externalRefLabel}
          showMetadataTab={showMetadataTab}
        />
      </div>

      <ReissuePaymentDialog
        open={reissueOpen}
        onOpenChange={setReissueOpen}
        eligibility={eligibility}
        submitting={reissueSubmitting}
        onSubmit={handleReissue}
        onOpenSupplier={() => navigate(supplierDetailPath)}
        onRefreshEligibility={loadEligibility}
        eligibilityLoading={eligibilityLoading}
        {...(supplierName ? { supplierName } : {})}
      />
    </div>
  );
};
